import parseJsx, {attributesToProps, domToReact} from 'html-react-parser';
import {chakra} from '@chakra-ui/react';
import {RAW_COMPONENTS} from '@amblique/base/app/constants/raw-components';
import {inflector} from '@amblique/base/shared/helpers/inflector';

/**
 * @typedef {import('html-dom-parser').DOMNode} DOMNode
 * @typedef {import('html-react-parser').HTMLReactParserOptions} HTMLReactParserOptions
 */
export const jsxParser = {
  /**
   * @readonly
   */
  JSON_REGEX: /^(?:\{([\w\W]*)\}|\[([\w\W]*)\])$/,

  /**
   * @readonly
   */
  SELF_CLOSE_REGEX: /<(\w+)([^>]*?)\/?>/gm,

  /**
   * @readonly
   */
  RAW_TAGS: ['title', 'style', 'base', 'link', 'meta', 'script', 'noscript'],

  /**
   * @readonly
   */
  VOID_TAGS: ['area', 'br', 'col', 'embed', 'hr', 'img', 'input', 'param', 'source', 'track', 'wbr'],

  /**
   * @param {string} input
   * @param {HTMLReactParserOptions} parseOptions
   *
   * @returns {React.DOMElement|React.DOMElement[]}
   */
  parse(input, parseOptions = {}) {
    return parseJsx(this.fixSelfClose(input), {
      replace: (node, index) => this.replace(node, index, parseOptions),
      ...parseOptions,
    });
  },

  /**
   * @param {{[name: string]: string}} attribs
   *
   * @returns {{[name: string]: string}}
   */
  parseAttrs(attribs) {
    const camalizedAttrs = Object.keys(attribs).reduce((attrs, name) => {
      const jsonValue = attribs[name].match(this.JSON_REGEX);
      let value = attribs[name];

      if (jsonValue) {
        try {
          value = Function(`return ${jsonValue[1]}`)();
        } catch (error) {
          value = attribs[name];
        }
      }

      if (name[0] === '_') {
        attrs['_' + inflector.camelize(name)] = value;
      } else if (name.includes('-') && !/^(accept-charset|http-equiv|data-|aria-)/.test(name)) {
        attrs[inflector.camelize(name)] = value;
      } else {
        attrs[name] = value;
      }

      return attrs;
    }, {});

    return attributesToProps(camalizedAttrs);
  },

  /**
   * @param {string} input
   *
   * @returns {string}
   */
  fixSelfClose(input) {
    return input.replace(this.SELF_CLOSE_REGEX, (match, tag, attrs) => {
      const isSelfClose = match.slice(-2) === '/>';
      const isVoidTag = this.VOID_TAGS.includes(tag);

      return isSelfClose && !isVoidTag ? `<${tag}${attrs}></${tag}>` : match;
    });
  },

  /**
   * Resolve `RAW_COMPONENTS` entry for a tag. Tries, in order:
   * `tag[role]`, `tag[type]` (same as legacy `role || type`), each `class` token as `tag[className]`, then bare `tag`.
   *
   * @param {string} name - Lowercase tag name from the parser
   * @param {{[name: string]: string}} attribs - Raw HTML attributes (`class`, not `className`)
   *
   * @returns {unknown} Registered component, tuple `[Component, defaults]`, or `undefined` if none
   */
  resolveRawComponent(name, attribs) {
    const tag = (name || '').toLowerCase();
    const keys = [];

    const roleOrType = attribs.role || attribs.type;

    if (roleOrType) {
      keys.push(`${tag}[${roleOrType}]`);
    }

    const classAttr = attribs.class || attribs.className;

    if (classAttr) {
      for (const cls of classAttr.trim().split(/\s+/)) {
        if (cls) {
          keys.push(`${tag}[${cls}]`);
        }
      }
    }

    keys.push(tag);

    const seen = new Set();

    for (const key of keys) {
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      if (Object.prototype.hasOwnProperty.call(RAW_COMPONENTS, key)) {
        return RAW_COMPONENTS[key];
      }
    }
  },

  /**
   * Replace the parsed DOM node into React node
   *
   * @param {DOMNode} node
   * @param {number} index
   * @param {HTMLReactParserOptions} parseOptions
   *
   * @returns {React.ReactNode}
   */
  replace(node, index, parseOptions) {
    const {type, name, attribs, children} = node;

    if (this.RAW_TAGS.includes(name)) {
      return node;
    }

    if (type === 'tag') {
      const isVoidTag = this.VOID_TAGS.includes(name);
      const rawComponent = this.resolveRawComponent(name, attribs) || [chakra[name]];
      const [Component, defaultProps = {}] = Array.isArray(rawComponent) ? rawComponent : [rawComponent];

      if (Component) {
        const props = {...defaultProps, ...this.parseAttrs(attribs)};
        const isEmpty = children.length === 0;

        return (
          isVoidTag || isEmpty ?
            <Component {...props} /> :
            <Component {...props}>
              {domToReact(children, {
                replace: (node, index) => this.replace(node, index, parseOptions),
                ...parseOptions,
              })}
            </Component>
        );
      }
    }
  },
};
